'use client';

import { IconButton, Modal, Typography } from '@mui/material';
import clsx from 'clsx';
import { animate } from 'motion';
import { useReducedMotion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { LuChevronLeft, LuChevronRight, LuX } from 'react-icons/lu';
const EASE_OUT = [0.23, 1, 0.32, 1];
const REST_CLIP = 'inset(0px 0px round 8px)';

/**
 * Transform + clip that lays the full frame exactly over its square, object-cover thumbnail: uniform scale (no
 * squash), clip-path crops the overflow the way object-cover does.
 */
function thumbnailPose(img, from) {
  const to = img.getBoundingClientRect();
  const s = Math.max(from.width / to.width, from.height / to.height);
  const dx = from.left + from.width / 2 - (to.left + to.width / 2);
  const dy = from.top + from.height / 2 - (to.top + to.height / 2);
  const ix = (to.width - from.width / s) / 2;
  const iy = (to.height - from.height / s) / 2;
  return {
    transform: `translate(${dx}px, ${dy}px) scale(${s})`,
    clipPath: `inset(${iy}px ${ix}px round ${6 / s}px)`
  };
}

/**
 * Fullscreen frame viewer. Opens by growing the clicked thumbnail into place and closes by shrinking back into the
 * current frame's thumbnail; stepping between frames is instant (keyboard-repeatable, so no motion).
 *
 * A nested MUI Modal so it stacks above the dialog and owns Esc + the focus trap. Its child must be a focusable
 * element (not AnimatePresence) or focus stays in the dialog below and Esc never reaches this Modal.
 */

export function FrameLightbox({
  images,
  index,
  onIndexChange,
  onClose,
  originFor
}) {
  const open = index !== null;
  const [mounted, setMounted] = useState(false);
  if (open && !mounted) setMounted(true);
  const reduce = useReducedMotion();
  const bgRef = useRef(null);
  const chromeRef = useRef(null);
  const imgRef = useRef(null);
  const openedRef = useRef(false);
  const closingRef = useRef(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (open) return;
    setMounted(false);
    setFailed(false);
    openedRef.current = false;
    closingRef.current = false;
  }, [open]);
  const origin = i => reduce ? null : originFor?.(i) ?? null;

  // Runs once per open, as soon as the frame has a laid-out size (immediately if cached, else on load).
  const runOpen = () => {
    const img = imgRef.current;
    if (!img || !img.naturalWidth || index === null || !bgRef.current || !chromeRef.current) return;
    if (openedRef.current) {
      // A frame stepped to after a failed one: show it without motion.
      img.style.opacity = '1';
      return;
    }
    openedRef.current = true;
    animate(bgRef.current, {
      opacity: [0, 1]
    }, {
      duration: 0.2,
      ease: EASE_OUT
    });
    animate(chromeRef.current, {
      opacity: [0, 1]
    }, {
      duration: 0.2,
      ease: EASE_OUT
    });
    const from = origin(index);
    if (from) {
      const pose = thumbnailPose(img, from);
      animate(img, {
        opacity: [1, 1],
        transform: [pose.transform, 'translate(0px, 0px) scale(1)'],
        clipPath: [pose.clipPath, REST_CLIP]
      }, {
        duration: 0.26,
        ease: EASE_OUT
      });
    } else {
      animate(img, {
        opacity: [0, 1]
      }, {
        duration: 0.2,
        ease: EASE_OUT
      });
    }
  };

  // An expired or broken URL never loads: open the viewer anyway (no zoom) so it isn't an invisible, click-eating layer.
  const handleError = () => {
    setFailed(true);
    if (openedRef.current || !bgRef.current || !chromeRef.current) return;
    openedRef.current = true;
    animate(bgRef.current, {
      opacity: [0, 1]
    }, {
      duration: 0.2,
      ease: EASE_OUT
    });
    animate(chromeRef.current, {
      opacity: [0, 1]
    }, {
      duration: 0.2,
      ease: EASE_OUT
    });
  };
  const requestClose = () => {
    if (closingRef.current || index === null) return;
    closingRef.current = true;
    const img = failed ? null : imgRef.current;
    const from = origin(index);
    const fast = {
      duration: 0.15,
      ease: EASE_OUT
    };
    const anims = [bgRef.current && animate(bgRef.current, {
      opacity: 0
    }, fast), chromeRef.current && animate(chromeRef.current, {
      opacity: 0
    }, {
      duration: 0.1
    })];
    if (img) {
      if (from) {
        const pose = thumbnailPose(img, from);
        anims.push(animate(img, {
          transform: pose.transform,
          clipPath: pose.clipPath
        }, {
          duration: 0.18,
          ease: EASE_OUT
        }));
      } else {
        anims.push(animate(img, {
          opacity: 0
        }, fast));
      }
    }
    void Promise.all(anims).then(onClose);
  };
  const step = d => {
    if (index === null || closingRef.current) return;
    setFailed(false);
    onIndexChange((index + d + images.length) % images.length);
  };
  const glass = 'pointer-events-auto bg-white/10! text-white! hover:bg-white/20!';
  return <Modal open={mounted} onClose={requestClose} hideBackdrop>
            <div className="fixed inset-0 outline-none" tabIndex={-1} onKeyDown={e => {
      if (e.key === 'ArrowLeft') step(-1);else if (e.key === 'ArrowRight') step(1);
    }}>
                <div ref={bgRef} className="absolute inset-0 bg-black/90" style={{
        opacity: 0
      }} onClick={requestClose} />
                {index !== null && <>
                        <div className="pointer-events-none absolute inset-0 flex items-center justify-center px-20 pt-16 pb-16">
                            {/* No key: stepping swaps src on the same element, so it changes instantly. */}
                            <img ref={el => {
            imgRef.current = el;
            if (el?.complete) runOpen();
          }} onLoad={runOpen} onError={handleError} src={images[index]} alt={`Frame ${index + 1}`} className={clsx('pointer-events-auto max-h-full max-w-full rounded-lg', failed && 'hidden')} style={{
            opacity: 0
          }} />
                            {failed && <Typography variant="body2" className="text-white/70!">
                                    This frame couldn&apos;t be loaded.
                                </Typography>}
                        </div>
                        <div ref={chromeRef} className="pointer-events-none absolute inset-0" style={{
          opacity: 0
        }}>
                            <div className="flex items-center justify-between px-5 py-4 text-white">
                                <Typography variant="body2" color="inherit">
                                    Frame {index + 1} of {images.length}
                                </Typography>
                                <IconButton aria-label="Close frame" onClick={requestClose} className={glass}>
                                    <LuX />
                                </IconButton>
                            </div>
                            {images.length > 1 && <>
                                    <IconButton aria-label="Previous frame" onClick={() => step(-1)} className={`absolute! top-1/2 left-5 -translate-y-1/2 ${glass}`}>
                                        <LuChevronLeft />
                                    </IconButton>
                                    <IconButton aria-label="Next frame" onClick={() => step(1)} className={`absolute! top-1/2 right-5 -translate-y-1/2 ${glass}`}>
                                        <LuChevronRight />
                                    </IconButton>
                                </>}
                        </div>
                    </>}
            </div>
        </Modal>;
}